import { EqlFieldsAutoComplete } from '@modules/common/components/EqlFieldsAutoComplete';
import { getFormattedDisplayNameForEqlField } from '@modules/common/components/EqlFieldsAutoComplete/helpers';
import { FieldIcon } from '@modules/common/components/FieldIcon';
import {
  useFetchEQLFields,
  useIsUnifiedFieldsEnabled,
  useShouldRemoveFileUploadPrefix,
} from '@modules/common/hooks';
import { SelectButton } from '@modules/queryBuilder/components/SelectButton';
import {
  getFallbackDisplayNameForEqlField,
  getFieldOperators,
  getFilterValueType,
  removeQueryFilterNode,
  upsertQueryFilterNode,
} from '@modules/queryBuilder/helpers';
import { useFetchCustomFilter } from '@modules/queryBuilder/hooks';
import { QueryBuilderActionType } from '@modules/queryBuilder/store/queryBuilderActionType';
import { useQueryBuilderStore } from '@modules/queryBuilder/store/useQueryBuilderStore';
import { ButtonProps } from '@mui/material';
import { EqlField, EqlFieldValueType } from 'generated/graphql';
import { SyntheticEvent } from 'react';
import { NormalisedFilter } from 'v2/querybuilder/types/querystore';

interface QueryFilterFieldSelectButtonProps {
  filter: NormalisedFilter;
  onChange?: (field: string | null) => void;
  buttonColor: ButtonProps['color'];
  readOnly: boolean;
}

const POPPER_WIDTH = 400;

export function QueryFilterFieldSelectButton({
  filter,
  onChange,
  buttonColor,
  readOnly,
}: QueryFilterFieldSelectButtonProps) {
  const { fields } = useFetchEQLFields({
    fetchPolicy: 'cache-and-network',
  });
  const [fetchAndApplyFilters] = useFetchCustomFilter();
  const dispatch = useQueryBuilderStore(({ dispatch }) => dispatch);
  const nodes = useQueryBuilderStore(({ query }) => query.nodes);
  const errorFilterID = useQueryBuilderStore(
    ({ metadata }) => metadata.error?.filterID,
  );
  const eqlFieldByFieldID = useQueryBuilderStore(
    ({ metadata }) => metadata.eqlFieldByFieldID,
  );
  const shouldRemoveFileUploadPrefix = useShouldRemoveFileUploadPrefix();
  const isUnifiedFieldEnabled = useIsUnifiedFieldsEnabled();

  const getLabel = () => {
    if (filter.field === 'search') {
      return 'Text search';
    }
    const eqlField = fields.find(({ field }) => field === filter.field);
    return eqlField
      ? getFormattedDisplayNameForEqlField({
          eqlField,
          shouldRemoveFileUploadPrefix,
          isUnifiedFieldEnabled,
        })
      : getFallbackDisplayNameForEqlField({
          field: filter.field,
          shouldShowSource: true,
        });
  };

  const onFieldChange = async (
    _: SyntheticEvent,
    eqlField: EqlField | null,
  ) => {
    if (!eqlField) {
      return;
    }

    const newField = eqlField.field;

    if (filter.field === newField) {
      return;
    }

    if (eqlField.valueType === EqlFieldValueType.None) {
      const analysisID = eqlField.detail?.reference?.referenceID;
      await fetchAndApplyFilters(analysisID);
      return;
    }

    let updatedNodes;

    if (newField) {
      const operator = getFieldOperators({ field: newField })[0];

      updatedNodes = upsertQueryFilterNode({
        node: {
          ...filter,
          field: newField,
          value: {
            type: getFilterValueType(eqlField),
            values: [],
          },
          operator,
        },
        nodesByID: nodes,
        parentID: filter.parent,
      });
    } else {
      updatedNodes = removeQueryFilterNode({
        node: { ...filter },
        nodes,
        parentID: filter.parent,
      });
    }

    dispatch({
      type: QueryBuilderActionType.SET_QUERY,
      payload: {
        nodes: updatedNodes,
      },
    });

    dispatch({
      type: QueryBuilderActionType.SET_METADATA,
      payload: {
        eqlFieldByFieldID: {
          ...eqlFieldByFieldID,
          [eqlField.field]: {
            ...eqlFieldByFieldID[eqlField.field],
            field: eqlField,
          },
        },
      },
    });

    onChange?.(newField);
  };

  const shouldShowError = errorFilterID === filter.id;

  return (
    <SelectButton
      className="qb-filter-field-select-button"
      label={getLabel()}
      startIcon={
        <FieldIcon
          field={filter.field}
          iconURL={eqlFieldByFieldID[filter.field]?.field?.iconURL}
          fontSize="small"
          valueType={eqlFieldByFieldID[filter.field]?.field?.valueType}
          objectType={eqlFieldByFieldID[filter.field]?.field?.objectType}
        />
      }
      buttonColor={buttonColor}
      readOnly={readOnly}
      componentsProps={{
        popper: { sx: { width: POPPER_WIDTH }, keepMounted: true },
        button: {
          color: shouldShowError ? 'error' : undefined,
          sx: {
            maxWidth: 250,
          },
        },
      }}
      renderDropDownComponent={({ open, onClose }) => (
        <EqlFieldsAutoComplete
          id={`qb-filter-field-dropdown-${filter.id}`}
          open={open}
          componentsProps={{
            popper: { style: { width: POPPER_WIDTH }, keepMounted: true },
          }}
          onChange={onFieldChange}
          onClose={onClose}
          shouldExcludeCustomFilters
        />
      )}
    />
  );
}
